import { Switch } from '../components/ui';
import { formatINR, formatINRCompact, formatRate } from '../lib/format';

export interface PricingDraft {
  /** True when this school is priced by hand rather than at the plan's list price. */
  custom: boolean;
  /** Rupees per student per month, as typed. Converted to paise on submit. */
  rupees: string;
  note: string;
}

export const emptyPricingDraft: PricingDraft = { custom: false, rupees: '', note: '' };

/** Rupees typed by a human → integer paise. Returns null for blank/invalid input. */
export function draftToPaise(draft: PricingDraft): number | null {
  if (!draft.custom) return null;
  const value = Number(draft.rupees);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

export function paiseToRupeeString(paise: number | null): string {
  if (paise === null) return '';
  return (paise / 100).toFixed(paise % 100 === 0 ? 0 : 2);
}

/**
 * The per-school rate control: ₹ per student per month, either inherited from
 * the plan or negotiated for this school. Shows the resulting bill as you type,
 * because "₹22" and "₹22 × 1,540 × 12 = ₹4,06,560/year" land very differently.
 */
export function PricingFields({
  draft,
  onChange,
  planName,
  planListMonthlyPaise,
  students,
  billingCycle,
}: {
  draft: PricingDraft;
  onChange: (next: PricingDraft) => void;
  planName: string;
  /** The plan's list price per student per month. Null for a custom-priced plan. */
  planListMonthlyPaise: number | null;
  students: number;
  billingCycle: 'ANNUAL' | 'MONTHLY';
}) {
  const customPaise = draftToPaise(draft);
  const effectivePaise = customPaise ?? planListMonthlyPaise;
  const mustBeCustom = planListMonthlyPaise === null;

  const monthlyTotal = effectivePaise === null ? null : effectivePaise * students;
  const annualTotal = monthlyTotal === null ? null : monthlyTotal * 12;

  const deltaPct =
    customPaise !== null && planListMonthlyPaise
      ? ((customPaise - planListMonthlyPaise) / planListMonthlyPaise) * 100
      : null;

  return (
    <div
      style={{
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-input)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        background: 'var(--white)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="module-row__name">Rate per student · per month</div>
          <div className="module-row__desc">
            {mustBeCustom ? (
              <>
                <strong>{planName}</strong> has no list price — set this school's rate.
              </>
            ) : (
              <>
                {planName} list price is{' '}
                <strong>{formatRate(planListMonthlyPaise)}</strong> per student per month.
              </>
            )}
          </div>
        </div>
        <Switch
          checked={draft.custom || mustBeCustom}
          disabled={mustBeCustom}
          onChange={(next) => onChange({ ...draft, custom: next })}
          label="Use a negotiated rate for this school"
        />
      </div>

      {draft.custom || mustBeCustom ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12 }}>
            <label className="field">
              <span>₹ per student / month</span>
              <input
                inputMode="decimal"
                value={draft.rupees}
                autoFocus
                placeholder={planListMonthlyPaise ? paiseToRupeeString(planListMonthlyPaise) : '25'}
                onChange={(e) => onChange({ ...draft, rupees: e.target.value.replace(/[^\d.]/g, '') })}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 600 }}
              />
            </label>
            <label className="field">
              <span>Why (kept on the record)</span>
              <input
                value={draft.note}
                placeholder="Negotiated by Ratnesh · 3-year commitment"
                onChange={(e) => onChange({ ...draft, note: e.target.value })}
              />
            </label>
          </div>

          {deltaPct !== null ? (
            <div className="caption">
              {deltaPct === 0 ? (
                'Same as the list price.'
              ) : (
                <>
                  <span style={{ color: deltaPct < 0 ? 'var(--leaf)' : 'var(--marigold-deep)', fontWeight: 700 }}>
                    {deltaPct < 0 ? `${Math.abs(deltaPct).toFixed(1)}% discount` : `${deltaPct.toFixed(1)}% above list`}
                  </span>{' '}
                  against the {planName} list price.
                </>
              )}
            </div>
          ) : null}
        </>
      ) : null}

      <div
        style={{
          display: 'flex',
          gap: 24,
          borderTop: '1px solid var(--rule)',
          paddingTop: 12,
          flexWrap: 'wrap',
        }}
      >
        <Total label="Rate" value={effectivePaise === null ? 'Not set' : `${formatRate(effectivePaise)}/student/mo`} />
        <Total label="Students" value={students > 0 ? students.toLocaleString('en-IN') : '—'} />
        <Total
          label="Per month"
          value={monthlyTotal === null || students === 0 ? '—' : formatINRCompact(monthlyTotal)}
        />
        <Total
          label="Per year"
          value={annualTotal === null || students === 0 ? '—' : formatINRCompact(annualTotal)}
          emphasis
        />
        <Total
          label={billingCycle === 'ANNUAL' ? 'Each invoice (annual)' : 'Each invoice (monthly)'}
          value={
            monthlyTotal === null || students === 0
              ? '—'
              : formatINR(billingCycle === 'ANNUAL' ? annualTotal! : monthlyTotal)
          }
        />
      </div>
    </div>
  );
}

function Total({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="label" style={{ fontSize: 10.5 }}>
        {label}
      </span>
      <span
        className="mono"
        style={{ fontSize: emphasis ? 18 : 15, fontWeight: 600, color: emphasis ? 'var(--leaf)' : undefined }}
      >
        {value}
      </span>
    </div>
  );
}
